import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Navbar from '../components/common/Navbar';
import LoadingScreen from '../components/common/loader';
import Cursor from '../components/common/cusor';
import ProgressScroll from '../components/common/ProgressScroll';
import Lines from '../components/common/Lines';
import '../components/brands/Generate.css';
import surveyData from '../data/portfolios/survey.json';
import { addSurveyResponse } from '../services/surveyService';
import { auth } from '../config/firebase';

function renderSurveyField(item, i, answers, setAnswers, disabled) {
  switch (item.type) {
    case 'text':
      return (
        <div className="input-group" key={i} style={{width: '100%', marginBottom: 24}}>
          <label className="question" style={{marginBottom: 10, display: 'block'}}>{item.question}</label>
          <input
            className="form-control"
            type="text"
            placeholder={item.placeholder || ''}
            value={answers[i] || ''}
            onChange={e => setAnswers(a => ({ ...a, [i]: e.target.value }))}
            disabled={disabled}
            style={{width: '100%', display: 'block', marginTop: 8}}
          />
        </div>
      );
    case 'radio':
      return (
        <div className="radio-group" key={i}>
          <label className="question" style={{marginBottom: 10}}>{item.question}</label>
          <div className="radio-options" style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px'}}>
            {item.options.map((option, idx) => (
              <label
                key={idx}
                className="radio-label"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '18px 20px',
                  backgroundColor: answers[i] === option ? 'rgba(253, 91, 56, 0.12)' : 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  border: answers[i] === option ? '2px solid #fd5b38' : '1px solid rgba(255, 255, 255, 0.1)',
                  fontWeight: 500,
                  fontSize: 16,
                  color: '#fff',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  minHeight: 48
                }}
                onClick={() => !disabled && setAnswers(a => ({ ...a, [i]: option }))}
              >
                <span style={{
                  display: 'inline-block',
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  border: '2px solid rgba(255,255,255,0.3)',
                  background: answers[i] === option ? '#fd5b38' : 'transparent',
                  marginRight: 10
                }}></span>
                {option}
                <input type="radio" name={`survey-radio-${i}`} checked={answers[i] === option} readOnly disabled={disabled} style={{position: 'absolute', opacity: 0, cursor: 'pointer', height: 0, width: 0}} />
              </label>
            ))}
          </div>
          {item.otherOption && answers[i] === 'Other' && (
            <div className="input-group" style={{width: '100%', marginTop: 16}}>
              <input
                className="form-control"
                type="text"
                placeholder={item.otherOption.placeholder || 'Please specify'}
                value={answers[`${i}_other`] || ''}
                onChange={e => setAnswers(a => ({ ...a, [`${i}_other`]: e.target.value }))}
                disabled={disabled}
                style={{
                  width: '100%',
                  display: 'block',
                  padding: '12px 16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: 16
                }}
              />
            </div>
          )}
        </div>
      );
    case 'slider':
      return (
        <div className="sliders-group" key={i}>
          <div className="slider-item">
            <label className="question" style={{marginBottom: 10}}>{item.question}</label>
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 10}}>
              <span style={{color: '#fff', fontSize: 14}}>{item.labels?.[0]}</span>
              <span style={{color: '#fff', fontSize: 14}}>{item.labels?.[1]}</span>
            </div>
            <input
              className="slider"
              type="range"
              min={item.min}
              max={item.max}
              value={answers[i] || Math.floor((item.min + item.max) / 2)}
              onChange={e => setAnswers(a => ({ ...a, [i]: Number(e.target.value) }))}
              disabled={disabled}
              style={{width: '100%'}}
            />
          </div>
        </div>
      );
    default:
      return null;
  }
}

export default function SurveyScreen() {
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('You must be signed in to submit feedback.');
      const userId = user.uid;
      const responseObj = {};
      surveyData.forEach((item, i) => {
        if (item.type === 'radio' && answers[i] === 'Other' && answers[`${i}_other`]) {
          responseObj[item.title] = answers[`${i}_other`];
        } else {
          responseObj[item.title] = answers[i] !== undefined ? answers[i] : '';
        }
      });
      await addSurveyResponse(userId, responseObj);
      setSubmitted(true);
      setAnswers({});
    } catch (err) {
      setError(err.message || 'Failed to submit feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewSubmission = () => {
    setSubmitted(false);
    setAnswers({});
    setError(null);
  };

  return (
    <>
      <Helmet>
        <title>NarrativeOS - Feedback Survey</title>
        <link rel="icon" href="/assets/imgs/favicon.ico" />
        <link rel="shortcut icon" href="/assets/imgs/favicon.ico" />
        <link rel="stylesheet" type="text/css" href="/assets/css/plugins.css" />
        <link rel="stylesheet" type="text/css" href="/assets/css/style.css" />
        <link
          rel="stylesheet"
          type="text/css"
          href="https://fonts.googleapis.com/css?family=Poppins:100,200,300,400,500,600,700,800,900&display=swap"
        />
        <link
          rel="stylesheet"
          type="text/css"
          href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@200;300;400;500;600;700&display=swap"
        />
        <script src="/assets/js/ScrollTrigger.min.js" defer />
        <script src="/assets/js/ScrollSmoother.min.js" defer />
        <script defer src="/assets/js/gsap.min.js"></script>
        <script defer src="/assets/js/splitting.min.js"></script>
        <script defer src="/assets/js/isotope.pkgd.min.js"></script>
        <script defer src="/assets/js/plugins.js"></script>
        <script defer src="/assets/js/TweenMax.min.js"></script>
        <script defer src="/assets/js/charming.min.js"></script>
        <script defer src="/assets/js/countdown.js"></script>
      </Helmet>
      <div className="home-personal">
        <LoadingScreen />
        <Cursor />
        <ProgressScroll />
        <Lines />
        <Navbar />
        <div id="smooth-wrapper">
          <div id="smooth-content">
            <main className="main-bg o-hidden">
              <div className="survey-container" style={{ maxWidth: 600, margin: '100px auto', padding: 32, background: 'rgba(20,20,20,0.98)', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.25)', color: '#fff' }}>
                <h2 style={{ textAlign: 'center', marginBottom: 32 }}>Feedback Survey</h2>
                {submitted ? (
                  <div style={{ color: '#fff', background: 'rgba(20,20,20,0.98)', borderRadius: 16, padding: 32, textAlign: 'center', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ fontSize: 22, fontWeight: 600, marginBottom: 12 }}>Thank you for your feedback!</div>
                    <div style={{ fontSize: 16, marginBottom: 24 }}>Your response has been recorded.</div>
                    <button 
                      onClick={handleNewSubmission}
                      className="submit-btn"
                      style={{ 
                        background: 'rgba(253, 91, 56, 0.12)',
                        border: '2px solid #fd5b38',
                        color: '#fff',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: 16,
                        fontWeight: 500
                      }}
                    >
                      Submit Another Response
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    {surveyData.map((item, i) => renderSurveyField(item, i, answers, setAnswers, submitting))}
                    {error && <div style={{ color: '#fd5b38', marginTop: 16 }}>{error}</div>}
                    <button type="submit" className="submit-btn" style={{ marginTop: 32 }} disabled={submitting}>
                      {submitting ? 'Submitting...' : 'Submit Survey'}
                    </button>
                  </form>
                )}
              </div>
            </main>
          </div>
        </div>
      </div>
      <style>
        {`
          @media (max-width: 600px) {
            .survey-container {
              padding: 16px !important;
              margin-top: 50px !important;
            }
          }
        `}
      </style>
    </>
  );
} 